import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, Hud } from "./Reveal";

const GROUPS = [
  ["speed", "Speed & Scheduling"],
  ["pricing", "Pricing & Payment"],
  ["service", "Service Details"],
];

const FILTERS = [
  ["all", "All Questions"],
  ...GROUPS,
];

const FAQS = [
  { q: "How fast can you come out?", a: "Same-day or next-day across most of North Metro Atlanta. Call early in the day and we'll do everything we can to get you cleared today — you'll get a text when the crew is on the way.", slug: "speed", tag: "speed" },
  { q: "How does pricing work?", a: "Simple load-based pricing — you pay for the space your junk takes in the trailer, from a minimum load up to a full one. You'll always approve an upfront price before we lift a finger. No hidden fees, ever.", slug: "pricing", tag: "pricing" },
  { q: "How do I get an exact price?", a: "Three easy ways: text us a photo of the pile at (470) 684-0150, use the quote form on this page, or have us take a look in person. You approve the exact price before we start, and it never changes after that.", slug: "exact-price", tag: "pricing" },
  { q: "What won't you take?", a: "We can't haul hazardous materials — chemicals, wet paint, asbestos, fuels, or anything illegal. Not sure about an item? Ask us and we'll point you to the right disposal option.", slug: "wont-take", tag: "service" },
  { q: "Are you licensed and insured?", a: "Yes — fully licensed and insured. Your home and property are protected on every single job, and every job is backed by our $10,000 Make It Right Guarantee.", slug: "insured", tag: "service" },
  { q: "Can you remove just one item?", a: "Absolutely — single-item pickups are some of our most common jobs. One sofa, one fridge, one mattress: our minimum load covers it, and we're in and out fast.", slug: "single-item", tag: "service" },
  { q: "What happens to the stuff you haul?", a: "Around 70% of what we haul gets donated or recycled — usable furniture and appliances go to local charities, metals and electronics get recycled, and only true junk goes to disposal. Your old stuff does some good on the way out.", slug: "where-stuff", tag: "service" },
  { q: "Do I need to be home?", a: "No. Curbside and contactless pickups work great — text us a photo, approve the quote, and we'll haul it and send confirmation photos when it's done.", slug: "home", tag: "service" },
  { q: "Do you clean up after the job?", a: "Every single time. We sweep the area, rake up the small debris, and leave the space cleaner than we found it. It's part of the job, not an extra.", slug: "cleanup", tag: "service" },
  { q: "What areas do you serve?", a: "Acworth, Kennesaw, Marietta, Woodstock, Canton, Smyrna, Mableton, Powder Springs, Austell and surrounding North Metro Atlanta. Just outside the list? Call — we probably still come.", slug: "areas", tag: "service" },
  { q: "How does the \"$50 Off Your First Haul\" yard sign offer work?", a: "It's up to $50 off your first haul, based on the quoted price before the discount: save $25 on a first haul quoted at $199–$249, or $50 on a first haul quoted at $250 or more. Hauls under $199 do not qualify. One discount per customer, applied to your first haul only — just mention the offer when you book.", slug: "first-haul-offer", tag: "pricing" },
  { q: "How do I pay?", a: "Card, cash, or digital payment — after the job is done and you're happy with it. Never before.", slug: "pay", tag: "pricing" },
  { q: "Do you offer same-day service?", a: "Yes — same-day and next-day are our specialty. The earlier you call or text, the better the odds we roll today.", slug: "same-day", tag: "speed" },
];

export const FAQ = () => {
  const [filter, setFilter] = useState("all");
  const tagsToShow = filter === "all" ? GROUPS.map(([tag]) => tag) : [filter];

  return (
    <section id="faq" data-testid="faq-section" className="relative bg-cover bg-center" style={{ backgroundImage: "url(/assets/gen/texture-concrete.jpg)" }}>
      <div className="texture-overlay absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-4xl px-5 py-24 md:px-8 md:py-32">
        <Reveal>
          <Hud>FAQ</Hud>
          <h2 className="mt-5 font-heading text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
            Straight Answers, <span className="text-gold">Before You Ask.</span>
          </h2>
        </Reveal>
        <Reveal>
          <div data-testid="faq-filters" className="mt-10 flex flex-wrap gap-2.5">
            {FILTERS.map(([slug, label]) => (
              <button
                key={slug}
                type="button"
                onClick={() => setFilter(slug)}
                data-testid={`faq-filter-${slug}`}
                aria-pressed={filter === slug}
                className={`border-2 px-5 py-2 font-body text-xs font-bold uppercase tracking-[0.15em] transition-all duration-200 ${
                  filter === slug
                    ? "border-gold bg-gold text-ink shadow-hard-sm"
                    : "border-coal bg-ink/60 text-white/70 hover:border-gold/60 hover:text-gold"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </Reveal>
        {tagsToShow.map((tag) => {
          const items = FAQS.filter((f) => f.tag === tag);
          if (!items.length) return null;
          const label = GROUPS.find(([t]) => t === tag)[1];
          return (
            <Reveal key={tag}>
              <div data-testid={`faq-group-${tag}`} className="mt-12">
                <p className="flex items-center gap-3 font-heading text-2xl uppercase tracking-wide text-gold md:text-3xl">
                  <span className="h-2.5 w-9 shrink-0 bg-gold" aria-hidden="true" />
                  {label}
                  <span className="font-body text-sm font-bold text-white/40">({items.length})</span>
                </p>
                <Accordion type="single" collapsible data-testid={`faq-accordion-${tag}`} className="mt-5 space-y-4">
                  {items.map((f, i) => (
                    <AccordionItem
                      key={f.slug}
                      value={f.slug}
                      data-testid={`faq-item-${f.slug}`}
                      className="border-2 border-coal bg-panel px-6 transition-all duration-200 hover:border-gold/50 data-[state=open]:border-gold data-[state=open]:shadow-hard-sm"
                    >
                      <AccordionTrigger
                        data-testid={`faq-trigger-${f.slug}`}
                        className="py-5 text-left font-heading text-xl uppercase tracking-wide text-white hover:text-gold hover:no-underline data-[state=open]:text-gold [&_svg]:text-gold md:text-2xl"
                      >
                        <span className="flex items-baseline gap-4">
                          <span className="font-body text-xs font-bold text-gold/70">{String(i + 1).padStart(2, "0")}</span>
                          {f.q}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent data-testid={`faq-content-${f.slug}`} className="pb-6 pl-9 text-sm capitalize leading-relaxed text-warmgray md:text-base">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </Reveal>
          );
        })}
        <Reveal delay={0.1}>
          <p data-testid="faq-more" className="mt-12 text-center font-body text-sm font-bold capitalize tracking-[0.05em] text-white/70">
            Still wondering something?{" "}
            <a href="#contact" data-testid="faq-contact-link" className="text-gold underline decoration-2 underline-offset-4 transition-colors duration-200 hover:text-gold-light">
              Ask us directly
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
};
